/**
 * Tiered Pricing Module
 * Handles dynamic price calculations based on variant and quantity
 * Supports variant-level and product-level tiered pricing metafields
 */

class TieredPricing {
  constructor(options = {}) {
    this.sectionId = options.sectionId;
    this.productData = options.productData || {};
    this.variantData = options.variantData || {};
    this.sqftValue = options.sqftValue || 1;
    this.currency = options.currency || 'USD';
    this.init();
  }

  init() {
    this.attachEventListeners();
    this.interceptAddToCart();
    this.updateCartItemPricing();
    this.updatePricingDisplay();
  }

  attachEventListeners() {
    // Watch for price element changes and correct them
    this.watchPriceElement();

    // Listen for variant changes via jQuery event (theme uses variantChange)
    const productContainer = document.querySelector('[data-section-type="product"]');
    if (productContainer && window.jQuery) {
      window.jQuery(productContainer).on('variantChange', () => {
        // Immediately update to tiered price to avoid any flash
        this.updatePricingDisplay();
        // Also update again after Shopify finishes its updates
        setTimeout(() => this.updatePricingDisplay(), 10);
      });
    }

    // Also listen for variant selection changes on the product form as fallback
    const variantSelect = document.querySelector('[name="id"]');
    if (variantSelect) {
      variantSelect.addEventListener('change', () => {
        // Immediately update to tiered price
        this.updatePricingDisplay();
      });
    }

    // Listen for quantity changes
    const quantityInput = document.querySelector(`#quantity, [name="quantity"]`);
    if (quantityInput) {
      quantityInput.addEventListener('change', () => this.updatePricingDisplay());
      quantityInput.addEventListener('input', () => this.updatePricingDisplay());
    }

    // Also handle plus/minus buttons - delay to let theme update the input value first
    const qtyBtns = document.querySelectorAll('.qtyBtn');
    qtyBtns.forEach(btn => {
      btn.addEventListener('click', () => {
        setTimeout(() => this.updatePricingDisplay(), 50);
      });
    });
  }

  /**
   * Watch the price element and correct it if it changes
   */
  watchPriceElement() {
    const self = this;
    const observerConfig = { childList: true, characterData: true, subtree: true };

    const getTieredPrice = () => {
      const currentVariant = self.getCurrentVariant();
      const quantity = self.getQuantity();
      if (!currentVariant) return null;
      const tieredConfig = self.getTieredPricingConfig(currentVariant);
      if (!tieredConfig) return null;
      return self.calculateTieredPrice(quantity, tieredConfig);
    };

    const getSqftPrice = () => {
      const currentVariant = self.getCurrentVariant();
      const quantity = self.getQuantity();
      if (!currentVariant) return null;
      const tieredConfig = self.getTieredPricingConfig(currentVariant);
      if (!tieredConfig) return null;
      const tier = self.getMatchingTier(quantity, tieredConfig);
      if (!tier || !tier.price_per_sqft) return null;
      return parseFloat(tier.price_per_sqft.toString().replace(/,/g, ''));
    };

    const correctSqftPrice = () => {
      const sqftPrice = getSqftPrice();
      if (!sqftPrice) return;
      const expected = self.formatPrice(sqftPrice);
      document.querySelectorAll('.product-price__tilesqft:not(.reg-price)').forEach((el) => {
        if (el.textContent.trim() !== expected) {
          el.innerHTML = expected;
        }
      });
    };

    // Watch #price-subtotal
    const priceElement = document.querySelector('#price-subtotal');
    if (priceElement) {
      const subtotalObserver = new MutationObserver(() => {
        const tieredPrice = getTieredPrice();
        if (!tieredPrice) return;
        const quantity = self.getQuantity();
        const expected = self.formatPrice(tieredPrice * quantity);
        if (priceElement.textContent !== expected) {
          subtotalObserver.disconnect();
          priceElement.textContent = expected;
          // Keep data-price in sync on ALL variant price elements
          document.querySelectorAll('.kanopi-current-variant-price').forEach((el) => {
            el.setAttribute('data-price', tieredPrice.toFixed(2));
            if (window.jQuery) window.jQuery(el).data('price', tieredPrice.toFixed(2));
          });
          subtotalObserver.observe(priceElement, observerConfig);
        }
      });
      subtotalObserver.observe(priceElement, observerConfig);
    }

    // Watch .variant-price-change and its parent for when theme replaces content
    const variantPriceEl = document.querySelector('.variant-price-change');
    if (variantPriceEl) {
      let correcting = false;

      const correctVariantPrice = () => {
        if (correcting) return;
        const tieredPrice = getTieredPrice();
        if (!tieredPrice) return;
        const expected = self.formatPrice(tieredPrice);
        const current = variantPriceEl.textContent.trim();
        if (current !== expected) {
          correcting = true;
          variantPriceEl.innerHTML = expected;
          correcting = false;
        }
      };

      const variantObserver = new MutationObserver(correctVariantPrice);
      variantObserver.observe(variantPriceEl, observerConfig);
    }

    // Watch ALL .kanopi-current-variant-price elements for smooth updates
    document.querySelectorAll('.kanopi-current-variant-price').forEach((el) => {
      let correcting = false;

      const correctKanopiPrice = () => {
        if (correcting) return;
        const tieredPrice = getTieredPrice();
        if (!tieredPrice) return;
        const quantity = self.getQuantity();
        const totalPrice = tieredPrice * quantity;
        const expected = self.formatPrice(totalPrice);
        const current = el.textContent.trim();
        if (current !== expected) {
          correcting = true;
          el.textContent = expected;
          el.setAttribute('data-price', tieredPrice.toFixed(2));
          if (window.jQuery) window.jQuery(el).data('price', tieredPrice.toFixed(2));
          correcting = false;
        }
      };

      const kanopiObserver = new MutationObserver(correctKanopiPrice);
      kanopiObserver.observe(el, observerConfig);
    });

    // Watch .js-optional-price for theme re-hiding it via handlePricePerUnitVisibility
    document.querySelectorAll('.js-optional-price').forEach((el) => {
      const visObserver = new MutationObserver(() => {
        const sqftPrice = getSqftPrice();
        if (sqftPrice && el.classList.contains('tw-hidden')) {
          el.classList.remove('tw-hidden');
        }
      });
      visObserver.observe(el, { attributes: true, attributeFilter: ['class'] });
    });
  }

  /**
   * Update pricing display - called after theme updates price
   */
  updatePricingDisplay() {
    const currentVariant = this.getCurrentVariant();
    const quantity = this.getQuantity();

    if (!currentVariant) return;
    
    const tieredConfig = this.getTieredPricingConfig(currentVariant);

    if (tieredConfig) {
      const tier = this.getMatchingTier(quantity, tieredConfig);
      const tieredPrice = this.calculateTieredPrice(quantity, tieredConfig);
      
      if (tieredPrice) {
        const totalPrice = tieredPrice * quantity;
        
        const priceElement = document.querySelector('#price-subtotal');
        if (priceElement) {
          priceElement.textContent = this.formatPrice(totalPrice);
        }

        // Update data-price AND text content on ALL variant price elements (main + sticky banners)
        // The persistent MutationObserver in watchPriceElement() will keep these correct
        const formattedTotal = this.formatPrice(totalPrice);
        document.querySelectorAll('.kanopi-current-variant-price').forEach((el) => {
          // Immediately update - the persistent observer will prevent any flash
          el.setAttribute('data-price', tieredPrice.toFixed(2));
          if (window.jQuery) window.jQuery(el).data('price', tieredPrice.toFixed(2));
          el.textContent = formattedTotal;
        });

        // Update ALL per-unit variant price elements
        const allVariantPriceEls = document.querySelectorAll('.variant-price-change');
        allVariantPriceEls.forEach((el) => {
          el.innerHTML = this.formatPrice(tieredPrice);
        });

        // Keep the cart property in sync with current tiered price
        this.updateCartProperty(tieredPrice);

        // Update price per sqft from the matching tier
        if (tier && tier.price_per_sqft) {
          const sqftPrice = parseFloat(tier.price_per_sqft.toString().replace(/,/g, ''));
          document.querySelectorAll('.product-price__tilesqft:not(.reg-price)').forEach((el) => {
            el.innerHTML = this.formatPrice(sqftPrice);
          });
          // Ensure the sqft wrapper is visible
          document.querySelectorAll('.js-optional-price').forEach((el) => {
            el.classList.remove('tw-hidden');
          });
        }
      }
    }
  }

  /**
   * Update pricing for all cart items based on their quantities
   * This handles multiple items in the cart with different quantities
   */
  updateCartItemPricing() {
    if (typeof window.CartJS === 'undefined' || !window.CartJS.cart) {
      return;
    }

    const self = this;
    const cart = window.CartJS.cart;

    // Update pricing for each item in cart
    if (cart.items && cart.items.length > 0) {
      cart.items.forEach(item => {
        // Check if this item has tiered pricing enabled
        if (item.properties && item.properties.__quantityBasedPricing === 'yes') {
          // Find the variant in product data to get tiered config
          const variant = self.findVariantById(item.variant_id);
          
          if (variant) {
            const tieredConfig = self.getTieredPricingConfig(variant);
            
            if (tieredConfig) {
              const tieredPrice = self.calculateTieredPrice(item.quantity, tieredConfig);
              
              if (tieredPrice) {
                const quantityBasedPrice = tieredPrice * item.quantity;
                
                // Update the cart item property via Shopify Cart API
                self.updateCartItemProperty(item.key, '__quantityBasedPricingValue', quantityBasedPrice);
              }
            }
          }
        }
      });
    }
  }

  /**
   * Update a specific cart item property via Shopify Cart API
   */
  updateCartItemProperty(itemKey, propertyName, propertyValue) {
    const updates = {};
    updates[itemKey] = {
      properties: {
        [propertyName]: propertyValue
      }
    };

    if (typeof Shopify !== 'undefined' && Shopify.cart && typeof Shopify.cart.updateItem === 'function') {
      Shopify.cart.updateItem(itemKey, { properties: { [propertyName]: propertyValue } });
    } else if (typeof CartJS !== 'undefined' && CartJS.updateCart) {
      // Alternative using CartJS
      CartJS.updateCart(updates);
    }
  }

  /**
   * Ensure the __quantityBasedPricing hidden input exists in the form
   * and keep it updated with the current tiered price
   */
  interceptAddToCart() {
    // Create the hidden input on init so it's always in the form when theme reads it
    const form = document.querySelector('.js-variants-form') || 
                 document.querySelector('form[action*="/cart/add"]');
    if (form) {
      let input = form.querySelector('input[name="properties[__quantityBasedPricing]"]');
      if (!input) {
        input = document.createElement('input');
        input.type = 'hidden';
        input.name = 'properties[__quantityBasedPricing]';
        input.value = '0';
        form.appendChild(input);
      }
      this._qtyPricingInput = input;
    }
  }

  /**
   * Update the __quantityBasedPricing hidden input with the current tiered price
   */
  updateCartProperty(tieredPrice) {
    if (this._qtyPricingInput) {
      this._qtyPricingInput.value = tieredPrice || '0';
    }
  }

  /**
   * Get the current selected variant
   */
  getCurrentVariant() {
    const variantSelect = document.querySelector('[name="id"]');
    if (variantSelect && variantSelect.value) {
      return this.findVariantById(variantSelect.value);
    }
    return null;
  }

  /**
   * Find variant by ID from product data
   */
  findVariantById(variantId) {
    if (this.productData.variants) {
      return this.productData.variants.find(v => v.id == variantId);
    }
    return null;
  }

  /**
   * Get quantity from input
   */
  getQuantity() {
    const quantityInput = document.querySelector(`#quantity, [name="quantity"]`);
    return quantityInput ? parseInt(quantityInput.value) || 1 : 1;
  }

  /**
   * Parse tiered pricing metafield
   * Expected format: { enabled, tiers: [{ qty, price_per_unit, price_per_sqft, price }] }
   */
  parseTieredPricing(metafieldValue) {
    if (!metafieldValue) return null;
    
    try {
      // Handle both direct values and .value property (for "One" type metafields)
      const value = metafieldValue.value || metafieldValue;
      
      if (typeof value === 'string') {
        return JSON.parse(value);
      }
      return value;
    } catch (e) {
      console.warn('Failed to parse tiered pricing metafield:', e);
      return null;
    }
  }

  /**
   * Get tiered pricing config for current variant
   * Supports both variant-level and product-level pricing with variant identification
   */
  getTieredPricingConfig(variant) {
    if (!variant) return null;

    // Check for variant-specific tiered pricing first
    if (window.variantTieredPricing && window.variantTieredPricing[variant.id]) {
      const config = this.parseTieredPricing(window.variantTieredPricing[variant.id]);
      if (config && (config.enabled === undefined || config.enabled === true)) {
        return config;
      }
    }

    // Fall back to product-level tiered pricing
    if (window.tieredPricingConfig) {
      const config = this.parseTieredPricing(window.tieredPricingConfig);
      
      if (!config) {
        return null;
      }
      
      // Check if pricing is enabled (default to true if not specified)
      if (config.enabled === undefined || config.enabled === true) {
        return config;
      }
    }

    return null;
  }

  /**
   * Generate a key for variant identification based on option values
   */
  getVariantKey(variant) {
    if (!variant) return null;
    
    // Create a key from option values: "option1|option2|option3"
    const options = [variant.option1, variant.option2, variant.option3].filter(o => o).join('|');
    return options || null;
  }

  /**
   * Get the matching tier object for the given quantity
   */
  getMatchingTier(quantity, tieredConfig) {
    if (!tieredConfig || !tieredConfig.tiers || tieredConfig.tiers.length === 0) {
      return null;
    }

    const sortedTiers = [...tieredConfig.tiers].sort((a, b) => {
      const aQty = parseInt(a.qty || a.min);
      const bQty = parseInt(b.qty || b.min);
      return bQty - aQty;
    });

    for (const tier of sortedTiers) {
      const tierQty = parseInt(tier.qty || tier.min);
      if (quantity >= tierQty) {
        return tier;
      }
    }

    return null;
  }

  /**
   * Calculate price per unit based on quantity tier
   */
  calculateTieredPrice(quantity, tieredConfig) {
    if (!tieredConfig || !tieredConfig.tiers || tieredConfig.tiers.length === 0) {
      return null;
    }

    // Sort tiers by qty (descending) to find the applicable tier
    const sortedTiers = [...tieredConfig.tiers].sort((a, b) => {
      const aQty = parseInt(a.qty || a.min);
      const bQty = parseInt(b.qty || b.min);
      return bQty - aQty;
    });
    
    for (const tier of sortedTiers) {
      const tierQty = parseInt(tier.qty || tier.min);
      if (quantity >= tierQty) {
        // Support both new format (price_per_unit) and old format (price as per-unit)
        const priceStr = (tier.price_per_unit || tier.price || '0').toString().replace(/,/g, '');
        return parseFloat(priceStr);
      }
    }

    return null;
  }

  /**
   * Format price as currency
   */
  formatPrice(amount) {
    if (amount === null || amount === undefined) return '';
    
    const formatter = new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: this.currency,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    });
    
    return formatter.format(amount);
  }

  /**
   * Update all pricing displays
   */
  updatePricing(variant = null) {
    const currentVariant = variant || this.getCurrentVariant();
    const quantity = this.getQuantity();

    if (!currentVariant) return;

    const tieredConfig = this.getTieredPricingConfig(currentVariant);

    if (tieredConfig) {
      const tieredPrice = this.calculateTieredPrice(quantity, tieredConfig);
      
      if (tieredPrice) {
        this.updatePriceDisplay(tieredPrice, quantity, currentVariant);
      }
    }
  }

  /**
   * Update main price display
   */
  updatePriceDisplay(tieredPrice, quantity, variant) {
    // Calculate total price (unit price × quantity)
    const totalPrice = tieredPrice * quantity;
    
    // Try multiple selectors to find the price element
    const selectors = [
      '#price-subtotal',
      '[id*="ProductPrice"]',
      '[data-product-price]',
      '.product-price',
      'span[class*="Price"]'
    ];
    
    for (const selector of selectors) {
      const priceElement = document.querySelector(selector);
      if (priceElement) {
        priceElement.textContent = this.formatPrice(totalPrice);
        break;
      }
    }
  }


  /**
   * Get tiered pricing tiers for display
   */
  getTiersForDisplay(variant) {
    const config = this.getTieredPricingConfig(variant);
    if (!config || !config.tiers) return [];
    
    return config.tiers.map(tier => ({
      min: tier.min,
      price: this.formatPrice(parseFloat(tier.price))
    }));
  }
}

/**
 * Collection Page Tiered Pricing
 * Handles tiered pricing for product cards on collection pages
 * Reads config from Shopify.productMeta[variantId].tieredPricing
 * Updates hidden __quantityBasedPricing input and price display per card
 */
class CollectionTieredPricing {
  constructor() {
    this.currency = window.shopCurrency || 'USD';
    this.init();
  }

  init() {
    this.attachEventListeners();
    this.updateAllCards();
  }

  attachEventListeners() {
    // Listen for quantity changes on collection cards
    $(document).on('change input', '.collection-page-quantity', (e) => {
      const form = $(e.target).closest('form');
      this.updateCard(form);
    });

    // Handle plus/minus buttons - delay to let theme update the input value first
    $(document).on('click', '.collection-prod-grid .qtyBtn', (e) => {
      const form = $(e.target).closest('form');
      setTimeout(() => this.updateCard(form), 50);
    });
  }

  updateAllCards() {
    const self = this;
    $('.button-set .js-tiered-price-input').each(function() {
      const form = $(this).closest('form');
      self.updateCard(form);
    });
  }

  updateCard(form) {
    if (!form || !form.length) return;

    const variantId = form.find('input[name="id"]').val();
    const qtyInput = form.find('input[name="quantity"]');
    const quantity = parseInt(qtyInput.val()) || 1;
    const tieredInput = form.find('.js-tiered-price-input');

    if (!variantId || !tieredInput.length) return;

    const meta = typeof Shopify !== 'undefined' && Shopify.productMeta ? Shopify.productMeta[variantId] : null;
    if (!meta || !meta.tieredPricing) {
      tieredInput.val('0');
      return;
    }

    const config = meta.tieredPricing;
    if (config.enabled === false) {
      tieredInput.val('0');
      return;
    }

    const tieredPrice = this.calculateTieredPrice(quantity, config);
    if (tieredPrice) {
      tieredInput.val(tieredPrice);
      this.updatePriceDisplay(form, tieredPrice, config, quantity, variantId);
    } else {
      tieredInput.val('0');
    }
  }

  calculateTieredPrice(quantity, config) {
    if (!config || !config.tiers || config.tiers.length === 0) return null;

    const sortedTiers = [...config.tiers].sort((a, b) => {
      return parseInt(b.qty || b.min) - parseInt(a.qty || a.min);
    });

    for (const tier of sortedTiers) {
      const tierQty = parseInt(tier.qty || tier.min);
      if (quantity >= tierQty) {
        const priceStr = (tier.price_per_unit || tier.price || '0').toString().replace(/,/g, '');
        return parseFloat(priceStr);
      }
    }
    return null;
  }

  getMatchingTier(quantity, config) {
    if (!config || !config.tiers || config.tiers.length === 0) return null;

    const sortedTiers = [...config.tiers].sort((a, b) => {
      return parseInt(b.qty || b.min) - parseInt(a.qty || a.min);
    });

    for (const tier of sortedTiers) {
      if (quantity >= parseInt(tier.qty || tier.min)) return tier;
    }
    return null;
  }

  updatePriceDisplay(form, tieredPrice, config, quantity, variantId) {
    const card = form.closest('.grid-view-item');
    if (!card.length) return;

    const tier = this.getMatchingTier(quantity, config);

    // Update per-unit price (price_per_sqft display)
    if (tier && tier.price_per_sqft) {
      const sqftPrice = parseFloat(tier.price_per_sqft.toString().replace(/,/g, ''));
      card.find('.price_per_sqft').not('.reg-price').text(this.formatPrice(sqftPrice));
    }

    // Update carton price display
    card.find('.final-price').text(this.formatPrice(tieredPrice));
  }

  formatPrice(amount) {
    if (amount === null || amount === undefined) return '';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: this.currency,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount);
  }
}

/**
 * Get the tiered unit price for a given variant and quantity.
 * Returns null if no tiered pricing config exists.
 */
function getTieredUnitPrice(variantId, quantity) {
  var configs = window.variantTieredPricing;
  if (!configs || typeof configs !== 'object') return null;

  var config = configs[variantId];
  if (!config) return null;
  if (typeof config === 'string') {
    try { config = JSON.parse(config); } catch(e) { return null; }
  }
  if (config.enabled === false || !config.tiers || config.tiers.length === 0) return null;

  var sortedTiers = config.tiers.slice().sort(function(a, b) {
    return parseInt(b.qty || b.min) - parseInt(a.qty || a.min);
  });

  for (var i = 0; i < sortedTiers.length; i++) {
    if (quantity >= parseInt(sortedTiers[i].qty || sortedTiers[i].min)) {
      return parseFloat((sortedTiers[i].price_per_unit || '0').toString().replace(/,/g, ''));
    }
  }
  return null;
}

/**
 * Pre-emptively update __quantityBasedPricing on CartJS items BEFORE
 * CartJS sends the quantity change request. This ensures the Cart Transform
 * gets the correct tier price on the first pass — no price flash.
 */
function preUpdateTieredPricing(newQty, lineIndex, clickedEl) {
  if (typeof CartJS === 'undefined' || !CartJS.cart || !CartJS.cart.items) return;
  // lineIndex is 1-based from CartJS data-cart-update attribute
  var item = CartJS.cart.items[lineIndex - 1];
  if (!item || !item.properties || item.properties.__quantityBasedPricing === undefined) return;

  var newPrice = getTieredUnitPrice(item.variant_id, newQty);
  if (newPrice === null) return;

  var currentPrice = parseFloat(item.properties.__quantityBasedPricing);
  // If crossing a tier boundary, hide the price until the corrected price arrives
  if (!isNaN(currentPrice) && Math.abs(currentPrice - newPrice) > 0.001) {
    var cartItem = clickedEl ? clickedEl.closest('.cart-item, .cart__row') : null;
    if (cartItem) {
      var priceEls = cartItem.querySelectorAll('.item-pricing .tw-ml-auto, .cart-price');
      priceEls.forEach(function(el) { el.style.visibility = 'hidden'; });
    }
    // Also hide the cart subtotal
    var subtotalEls = document.querySelectorAll('[rv-text="cart.items | total_custom_rpa cart"], .cart-subtotal-row .product-price');
    subtotalEls.forEach(function(el) { el.style.visibility = 'hidden'; });
    item.properties.__quantityBasedPricing = newPrice.toString();
  }
}

/**
 * Attach pre-emptive click handlers on mini-cart +/- buttons.
 * These fire BEFORE CartJS processes the click (since we use capture phase).
 */
function attachCartTieredListeners() {
  // Mini-cart: +/- buttons have data-cart-update (line) and data-cart-quantity (new qty)
  document.addEventListener('click', function(e) {
    var el = e.target.closest('[data-cart-update]');
    if (!el) return;
    var lineIndex = parseInt(el.getAttribute('data-cart-update'));
    var newQty = parseInt(el.getAttribute('data-cart-quantity'));
    if (!isNaN(lineIndex) && !isNaN(newQty) && newQty > 0) {
      preUpdateTieredPricing(newQty, lineIndex, el);
    }
  }, true); // capture phase — fires before CartJS's jQuery handler

  // Also handle typed quantity input in mini-cart
  document.addEventListener('change', function(e) {
    var input = e.target;
    if (!input.matches || !input.matches('.qty-trigger')) return;
    var cartItemEl = input.closest('.cart-item, .cart__row');
    if (!cartItemEl) return;
    var newQty = parseInt(input.value);
    if (isNaN(newQty) || newQty < 1) return;
    // Find the line index from a sibling +/- button's data-cart-update
    var btn = cartItemEl.querySelector('[data-cart-update]');
    if (!btn) return;
    var lineIndex = parseInt(btn.getAttribute('data-cart-update'));
    if (isNaN(lineIndex)) return;
    if (typeof CartJS === 'undefined' || !CartJS.cart || !CartJS.cart.items) return;
    var item = CartJS.cart.items[lineIndex - 1];
    if (!item || !item.properties || item.properties.__quantityBasedPricing === undefined) return;
    var newPrice = getTieredUnitPrice(item.variant_id, newQty);
    if (newPrice === null) return;
    var currentPrice = parseFloat(item.properties.__quantityBasedPricing);
    if (!isNaN(currentPrice) && Math.abs(currentPrice - newPrice) > 0.001) {
      // Hide prices during tier boundary crossing
      var priceEls = cartItemEl.querySelectorAll('.item-pricing .tw-ml-auto, .cart-price');
      priceEls.forEach(function(el) { el.style.visibility = 'hidden'; });
      var subtotalEls = document.querySelectorAll('[rv-text="cart.items | total_custom_rpa cart"], .cart-subtotal-row .product-price');
      subtotalEls.forEach(function(el) { el.style.visibility = 'hidden'; });
      item.properties.__quantityBasedPricing = newPrice.toString();
    }
  }, true);
}

// Initialize on page load
document.addEventListener('DOMContentLoaded', function() {
  // PDP initialization
  let productSection = document.querySelector('[data-section-type="product"]');
  if (!productSection) {
    productSection = document.querySelector('[data-product-section]');
  }
  if (!productSection) {
    productSection = document.querySelector('.product-section');
  }
  if (!productSection) {
    productSection = document.querySelector('[id*="product"]');
  }
  
  if (productSection && window.productData) {
    window.tieredPricing = new TieredPricing({
      sectionId: productSection.getAttribute('data-section-id') || productSection.id,
      productData: window.productData,
      sqftValue: window.sqftValue || 1,
      currency: window.shopCurrency || 'USD'
    });
  }

  // Collection page initialization
  const collectionSection = document.querySelector('[data-section-type="collection-template"]');
  if (collectionSection && typeof Shopify !== 'undefined' && Shopify.productMeta) {
    window.collectionTieredPricing = new CollectionTieredPricing();
  }

  // Pre-emptively update tiered pricing in CartJS memory before the request,
  // AND do a follow-up server update + re-render after CartJS completes
  attachCartTieredListeners();

  if (typeof jQuery !== 'undefined') {
    var __tieredRecalcInProgress = false;
    jQuery(document).on('cart.requestComplete', function(event, cart) {
      if (__tieredRecalcInProgress) return;
      if (!cart || !cart.items) return;

      var updateQueue = [];
      cart.items.forEach(function(item, index) {
        if (!item.properties || item.properties.__quantityBasedPricing === undefined) return;
        var newPrice = getTieredUnitPrice(item.variant_id, item.quantity);
        if (newPrice === null) return;
        var currentPrice = parseFloat(item.properties.__quantityBasedPricing);
        if (isNaN(currentPrice) || Math.abs(currentPrice - newPrice) > 0.001) {
          var newProps = {};
          for (var key in item.properties) { newProps[key] = item.properties[key]; }
          newProps.__quantityBasedPricing = newPrice.toString();
          updateQueue.push({ line: index + 1, quantity: item.quantity, properties: newProps });
        }
      });

      if (updateQueue.length === 0) return;
      __tieredRecalcInProgress = true;

      function processNext() {
        if (updateQueue.length === 0) {
          __tieredRecalcInProgress = false;
          return;
        }
        var update = updateQueue.shift();
        jQuery.ajax({
          type: 'POST',
          url: '/cart/change.js',
          data: JSON.stringify({
            line: update.line,
            quantity: update.quantity,
            properties: update.properties
          }),
          contentType: 'application/json',
          dataType: 'json',
          success: function(updatedCart) {
            if (updateQueue.length > 0) {
              processNext();
            } else {
              if (typeof CartJS !== 'undefined') {
                CartJS.cart.items = updatedCart.items;
                CartJS.cart.total_price = updatedCart.total_price;
                CartJS.cart.total_discount = updatedCart.total_discount;
                CartJS.cart.item_count = updatedCart.item_count;
              }
              jQuery(document).trigger('cart.requestComplete', [updatedCart]);
              // Restore hidden prices after re-render
              setTimeout(function() {
                __tieredRecalcInProgress = false;
                document.querySelectorAll('.item-pricing .tw-ml-auto, .cart-price, [rv-text="cart.items | total_custom_rpa cart"], .cart-subtotal-row .product-price').forEach(function(el) {
                  el.style.visibility = '';
                });
              }, 100);
            }
          },
          error: function() { __tieredRecalcInProgress = false; }
        });
      }
      processNext();
    });
  }
});

// Export for use in other modules
if (typeof module !== 'undefined' && module.exports) {
  module.exports = TieredPricing;
}
